import Service, { type Registry as Services, service } from '@ember/service';
import { Crisp } from 'crisp-sdk-web';
import config from 'uplisting-frontend/config/environment';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export default class CrispService extends Service {
  @service featureFlags!: Services['feature-flags'];

  #configured = false;

  public get available(): boolean {
    return this.featureFlags.gate.enabled('crisp') && !!config.crisp.websiteId;
  }

  public configure(): void {
    if (this.#configured || !this.available) {
      return;
    }

    Crisp.configure(config.crisp.websiteId);

    Crisp.chat.hide();
    Crisp.chat.onChatClosed(() => Crisp.chat.hide());
    Crisp.chat.onChatOpened(() => Crisp.chat.show());

    this.#configured = true;
  }

  public openChat(): void {
    if (!this.#configured) {
      return;
    }

    Crisp.chat.show();
    Crisp.chat.open();
  }

  public identifyUser(user: ModelRegistry['user']): void {
    if (!this.#configured || !user) {
      return;
    }

    Crisp.user.setEmail(user.email, user.crispUserHash);
    Crisp.user.setNickname(user.name);

    const customer = user.customer;

    Crisp.session.setData({
      user_id: user.id,
      account_id: user.accountId,
      role: user.role,
      lapsed: user.lapsed,
      trialist: user.trialist,
      trial_status: user.trialStatus,
      onboard_status: user.onboardStatus ?? 'needs_info',
      created_at: user.createdAt.toISOString(),
      number_listings: user.numberListings,
      stripe_connected: user.stripeConnected,
      subscription_active: user.subscriptionActive,
      ...(user.trialEnd && { trial_end: user.trialEnd.toISOString() }),
      ...(user.lastActiveAt && {
        last_active_at: user.lastActiveAt.toISOString(),
      }),
      ...(user.vrboAdvertiserId && {
        vrbo_advertiser_id: user.vrboAdvertiserId,
      }),
      ...(customer && {
        plan_version: customer.planVersion,
        currency: customer.currency,
        annual: customer.annual,
        subscribed: customer.subscribed,
      }),
      ...(user.airbnbGeneralConnectionLevel && {
        direct_website_enabled: user.directWebsiteEnabled,
        airbnb_general_connection_level: user.airbnbGeneralConnectionLevel,
        number_airbnb_listings: user.numberAirbnbListings,
        number_bdc_listings: user.numberBdcListings,
        number_home_away_listings: user.numberHomeAwayListings,
        protect_feature_active: user.protectFeatureActive,
        identity_feature_active: user.identityFeatureActive,
        esign_feature_active: user.esignFeatureActive,
        client_management_enabled: user.clientManagementEnabled,
        client_statements_enabled: user.clientStatementsEnabled,
        dynamic_pricing_sync_active: user.dynamicPricingSyncActive,
        changeover_feature_active: user.changeoverFeatureActive,
      }),
    });
  }

  public reset(): void {
    this.#clearStorage();

    if (!this.#configured) {
      return;
    }

    Crisp.setTokenId();
    Crisp.session.reset();
  }

  #clearStorage(): void {
    const prefix = 'crisp-client/';

    for (const storage of [window.localStorage, window.sessionStorage]) {
      const keysToRemove: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);

        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
    }

    window.document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0]?.trim();

      if (name?.startsWith(prefix)) {
        window.document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    crisp: CrispService;
  }
}
